'use client';

export type VehicleKind = 'car' | 'truck' | 'bus';

interface VehicleSpriteProps {
  kind: VehicleKind;
  /** Body colour. Drawn from `VEHICLE_COLORS` so the prompt can name it. */
  color: string;
  width?: number;
}

/**
 * Chrome fills read from the theme variables rather than literals.
 *
 * `ThemeProvider` rewrites every gray at runtime from the tenant's Theme (`theme-utils.ts`
 * colorMappings), so a literal here would be the one part of the puzzle that ignored the
 * tenant's palette. Referenced as `var(...)` because these land on the SVG `fill` attribute,
 * where a Tailwind class cannot reach — and as the `--tw-color-*` form, which is what Tailwind
 * emits from `@theme` under `prefix(tw)`; the unprefixed `--color-*` is only set once a tenant
 * theme has been applied, so it is blank on the default palette.
 */
const WINDOW_FILL = 'var(--tw-color-gray-light)';
const WHEEL_FILL = 'var(--tw-color-gray-darker)';

/**
 * Top-down-ish side view of a vehicle, shared by every tap-a-vehicle puzzle (2h, 2k, 2o).
 *
 * Drawn as inline SVG rather than an icon or image because the body colour is the answer to the
 * challenge — it has to be settable per instance, and it changes on every reroll.
 */
export const VehicleSprite = ({ kind, color, width = 44 }: VehicleSpriteProps) => {
  const height = Math.round((width * 22) / 48);

  if (kind === 'truck') {
    return (
      <svg width={width} height={height} viewBox="0 0 48 22" aria-hidden="true">
        <rect x="4" y="8" width="11" height="9" rx="2" fill={color} />
        <rect x="6" y="10" width="6" height="4" rx="1" fill={WINDOW_FILL} />
        <rect x="16" y="3" width="28" height="14" rx="2" fill={color} />
        <circle cx="11" cy="18" r="3.5" fill={WHEEL_FILL} />
        <circle cx="36" cy="18" r="3.5" fill={WHEEL_FILL} />
      </svg>
    );
  }

  if (kind === 'bus') {
    return (
      <svg width={width} height={height} viewBox="0 0 48 22" aria-hidden="true">
        <rect x="4" y="3" width="40" height="14" rx="3" fill={color} />
        <rect x="8" y="6" width="7" height="5" rx="1" fill={WINDOW_FILL} />
        <rect x="17" y="6" width="7" height="5" rx="1" fill={WINDOW_FILL} />
        <rect x="26" y="6" width="7" height="5" rx="1" fill={WINDOW_FILL} />
        <circle cx="12" cy="18" r="3.5" fill={WHEEL_FILL} />
        <circle cx="36" cy="18" r="3.5" fill={WHEEL_FILL} />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} viewBox="0 0 48 22" aria-hidden="true">
      <rect x="4" y="9" width="40" height="8" rx="3" fill={color} />
      <rect x="13" y="3" width="19" height="9" rx="3" fill={color} />
      <rect x="16" y="5" width="6" height="5" rx="1.5" fill={WINDOW_FILL} />
      <rect x="24" y="5" width="6" height="5" rx="1.5" fill={WINDOW_FILL} />
      <circle cx="13" cy="18" r="3.5" fill={WHEEL_FILL} />
      <circle cx="35" cy="18" r="3.5" fill={WHEEL_FILL} />
    </svg>
  );
};

VehicleSprite.displayName = 'VehicleSprite';

/**
 * The draggable car used by the drag puzzles (1a, 2c) and the slide puzzles (1d).
 *
 * Body colour is the tenant's `primary` so the piece the customer manipulates is the one branded
 * element in the playfield — the surrounding bays stay neutral, which is also what makes the
 * highlighted target readable.
 */
export const DraggableCar = ({ shake }: { shake?: boolean }) => (
  <div
    className={[
      'tw:absolute tw:inset-0 tw:bg-primary',
      'tw:rounded-[14px_14px_10px_10px] tw:shadow-[0_6px_14px_rgba(38,48,61,0.35)]',
      shake ? 'tw:animate-[pb-captcha-shake_0.3s]' : '',
    ].join(' ')}
    aria-hidden="true"
  >
    <div className="tw:absolute tw:left-[7px] tw:right-[7px] tw:top-[14px] tw:h-[14px] tw:rounded-4 tw:bg-gray-light" />
    <div className="tw:absolute tw:bottom-micro tw:left-[9px] tw:right-[9px] tw:h-micro tw:rounded-2 tw:bg-gray-light tw:opacity-80" />
  </div>
);

DraggableCar.displayName = 'DraggableCar';
